"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { defaultLocale, locales, type Locale } from "@/i18n/config";

// Cache for loaded translations
const translationCache: Record<string, Record<string, unknown>> = {};

/**
 * Detect locale from URL path on client side
 */
function detectLocaleFromPath(): Locale {
  if (typeof window !== "undefined") {
    const pathLocale = window.location.pathname.split("/")[1];
    if (locales.includes(pathLocale as Locale)) {
      return pathLocale as Locale;
    }
    // Try to get from localStorage
    const stored = localStorage.getItem("preferred_locale");
    if (stored && locales.includes(stored as Locale)) {
      return stored as Locale;
    }
  }
  return defaultLocale;
}

/**
 * Client-side translation hook for pages outside the [locale] route.
 * Detects locale from URL path or falls back to defaultLocale.
 * 
 * @param namespace - The translation namespace (e.g., "tokenpg", "logpg")
 * @returns Translation function and current locale
 */
export function useClientTranslation(namespace: string) {
  // Track if we're on client side to handle hydration
  const [isClient, setIsClient] = useState(false);
  
  // Initialize with defaultLocale for SSR, will update on client
  const [locale, setLocale] = useState<Locale>(defaultLocale);
  const [translations, setTranslations] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Track the current pathname to detect navigation
  const pathnameRef = useRef<string>("");

  // Detect locale on client mount and handle navigation
  useEffect(() => {
    setIsClient(true);
    
    // Initial detection
    const detected = detectLocaleFromPath();
    setLocale(detected);
    pathnameRef.current = window.location.pathname;
    
    // Listen for popstate (browser back/forward) and custom navigation events
    const handleNavigation = () => {
      const currentPath = window.location.pathname;
      if (currentPath !== pathnameRef.current) {
        pathnameRef.current = currentPath;
        const newLocale = detectLocaleFromPath();
        setLocale(prev => prev !== newLocale ? newLocale : prev);
      }
    };
    
    // Handle browser navigation
    window.addEventListener("popstate", handleNavigation);
    
    // Handle Next.js route changes using MutationObserver on URL
    const checkUrlChange = () => {
      const currentPath = window.location.pathname;
      if (currentPath !== pathnameRef.current) {
        handleNavigation();
      }
    };
    
    // Poll for URL changes (for Next.js client-side routing)
    const intervalId = setInterval(checkUrlChange, 100);
    
    return () => {
      window.removeEventListener("popstate", handleNavigation);
      clearInterval(intervalId);
    };
  }, []);

  // Load translations for the namespace
  useEffect(() => {
    const loadTranslations = async () => {
      const cacheKey = `${locale}:${namespace}`;
      
      if (translationCache[cacheKey]) {
        setTranslations(translationCache[cacheKey]);
        setIsLoading(false);
        return;
      }

      try {
        // Dynamic import of the translation file
        const module = await import(`@/messages/${locale}/${namespace}.json`);
        const data = module.default || module;
        translationCache[cacheKey] = data;
        setTranslations(data);
      } catch (error) {
        console.warn(`Failed to load translations for ${locale}/${namespace}:`, error);
        // Try to load default locale as fallback
        if (locale !== defaultLocale) {
          try {
            const fallbackModule = await import(`@/messages/${defaultLocale}/${namespace}.json`);
            const fallbackData = fallbackModule.default || fallbackModule;
            setTranslations(fallbackData);
          } catch {
            setTranslations({});
          }
        } else {
          setTranslations({});
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [locale, namespace]);

  /**
   * Get translation value by dot-notation key
   * @param key - Dot-notation key (e.g., "tableHeaders.tokenName")
   * @param params - Optional parameters for interpolation
   */
  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    // Return non-breaking space while loading to prevent raw keys from flashing
    if (isLoading) return "\u00A0";
    const keys = key.split(".");
    let value: unknown = translations;
    
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        // Key not found, return the key itself
        return key;
      }
    }

    if (typeof value !== "string") {
      return key;
    }

    // Handle parameter interpolation (e.g., {name})
    if (params) {
      return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
        return params[paramKey]?.toString() ?? `{${paramKey}}`;
      });
    }

    return value;
  }, [translations]);

  return { t, locale, isLoading, isClient };
}

export default useClientTranslation;
